// src/context/AuthContext.js
import { createContext, useContext, useEffect, useState } from 'react';
import apiService from '../services/apiService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for stored auth token on app start
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('userData');
    
    console.log('AuthContext: Checking stored auth data...', { 
      hasToken: !!token, 
      hasUserData: !!userData 
    });
    
    if (token && userData) {
      try {
        const parsedUserData = JSON.parse(userData);
        console.log('AuthContext: Restoring user from localStorage:', parsedUserData);
        setUser(parsedUserData);
        
        // Optionally verify token with backend (comment out if not needed)
        // verifyToken(token);
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
      }
    }
    setLoading(false);
  }, []);

  // Verify token validity with backend
  const verifyToken = async (token) => {
    try {
      const data = await apiService.verifyToken();
      
      if (data.success && data.user) {
        // Update user data if needed
        setUser(data.user);
        localStorage.setItem('userData', JSON.stringify(data.user));
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      // Clear invalid token
      logout();
    }
  };

  const login = async (credentials) => {
    try {
      console.log('AuthContext: Attempting login with credentials:', { email: credentials.email });
      
      // Use the API service instead of direct fetch
      const data = await apiService.login(credentials);
      
      console.log('AuthContext: Login response received:', data);

      if (data.success) {
        // Extract user and token from the response
        const { user: userData, token } = data;
        
        console.log('AuthContext: Extracted data:', { userData, token });
        
        // Validate that we have the required data
        if (!userData || !token) {
          throw new Error('Invalid response format: missing user data or token');
        }
        
        // Store authentication data
        localStorage.setItem('authToken', token);
        localStorage.setItem('userData', JSON.stringify(userData));
        
        console.log('AuthContext: Stored to localStorage successfully');
        
        // Update state
        setUser(userData);
        
        console.log('AuthContext: Login successful, user set:', userData);
        return userData;
      } else {
        // Handle case where success is false
        const errorMessage = data.message || 'Login failed';
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('AuthContext: Login error:', error);
      
      // Re-throw the error so the LoginScreen can handle it
      throw error;
    }
  };

const register = async (userData) => {
  try {
    console.log('AuthContext: Attempting registration with data:', userData);
    
    // Use the API service for registration
    const data = await apiService.register(userData);
    
    console.log('AuthContext: Registration response received:', data);

    if (data.success || data.token) {
      // Extract user and token from the response
      const { user: registeredUser, token } = data;
      
      console.log('AuthContext: Registration successful:', { registeredUser, token });
      
      // Validate that we have the required data
      if (!registeredUser || !token) {
        throw new Error('Invalid response format: missing user data or token');
      }
      
      // Store authentication data
      localStorage.setItem('authToken', token);
      localStorage.setItem('userData', JSON.stringify(registeredUser));
      
      console.log('AuthContext: Stored to localStorage successfully');
      
      // Update state
      setUser(registeredUser);
      
      console.log('AuthContext: Registration complete, user set:', registeredUser);
      return registeredUser;
    } else {
      // Handle case where success is false
      const errorMessage = data.message || 'Registration failed';
      throw new Error(errorMessage);
    }
  } catch (error) {
    console.error('AuthContext: Registration error:', error);
    throw error;
  }
};


  const logout = async () => {
    console.log('AuthContext: Logging out...');
    
    const token = localStorage.getItem('authToken');
    
    // Optional: Call logout endpoint to invalidate token on server
    if (token) {
      try {
        await apiService.logout();
        console.log('AuthContext: Server logout successful');
      } catch (error) {
        console.error('Logout API call failed:', error);
        // Continue with local logout even if API call fails
      }
    }

    // Clear local storage and state
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('rememberLogin');
    setUser(null);
    
    console.log('AuthContext: Local logout completed');
  };

  // Function to get auth headers for other API calls
  const getAuthHeaders = () => {
    return apiService.getAuthHeaders();
  };

  // Function to make authenticated API calls
  const apiCall = async (endpoint, options = {}) => {
    return apiService.makeRequest(endpoint, options);
  };

  // Function to update user data (useful for profile updates)
  const updateUser = (updatedUserData) => {
    const newUserData = { ...user, ...updatedUserData };
    setUser(newUserData);
    localStorage.setItem('userData', JSON.stringify(newUserData));
  };

  const value = {
    user,
    login,
    register,
    logout,
    loading,
    getAuthHeaders,
    apiCall,
    updateUser,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};